/**
 * The staff member's own time clock in the staff app (My Schedule): clock in (pick the job
 * when there are several), start a paid / unpaid break, end it, clock out, and today's
 * hours. The staff session (pos_token) is the proof — no PIN. Same clock as the door's
 * (labor-service /api/timeclock/v2 -> time_clock_entries, the rows Payroll pays). Offline,
 * the punch is kept on the phone and sent when it is back online.
 *
 * Other parts of the page start a punch with
 *   window.dispatchEvent(new CustomEvent(MY_CLOCK_ACTION, { detail: { action: 'CLOCK_IN' } }))
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Briefcase, CloudOff, Coffee, Loader2, LogIn, LogOut, Play, Timer, UtensilsCrossed } from 'lucide-react';
import { toast } from 'sonner';
import { ACTION_LABEL, STATE_LABEL, timeClockApi } from '@/lib/timeclock/api';
import { deviceId, newClientPunchId, queuePunch, startAutoSync, pendingCount, QUEUE_CHANGED } from '@/lib/timeclock/punchQueue';
import { fmtDuration, fmtTime } from '@/lib/timeclock/time';

export const MY_CLOCK_ACTION = 'chain29-my-clock-action';

const staffHeaders = () => {
  const token = localStorage.getItem('pos_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function staffClaims() {
  try {
    const p = JSON.parse(atob(localStorage.getItem('pos_token').split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return p.typ === 'staff' ? { staffId: Number(p.user_id || p.sub), restaurantId: Number(p.restaurant_id) } : null;
  } catch (e) {
    return null;
  }
}

const BADGE = {
  OFF: 'bg-slate-700/60 text-slate-200',
  WORKING: 'bg-emerald-500/20 text-emerald-300',
  ON_BREAK: 'bg-amber-500/20 text-amber-300',
};

export default function MyClockCard() {
  const api = useMemo(() => timeClockApi(staffHeaders), []);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [chooser, setChooser] = useState(null);
  const [pending, setPending] = useState(0);
  const claims = staffClaims();

  const load = useCallback(async () => {
    if (!claims) return;
    try {
      setInfo(await api.state());
      setError(null);
    } catch (e) {
      setError(e.network ? 'Offline — punches are kept on this phone and sent when it is back online.' : e.message);
    }
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    startAutoSync();
    const refresh = async () => { setPending(await pendingCount()); load(); };
    refresh();
    window.addEventListener(QUEUE_CHANGED, refresh);
    window.addEventListener('online', refresh);
    const t = setInterval(load, 60000);
    return () => {
      window.removeEventListener(QUEUE_CHANGED, refresh);
      window.removeEventListener('online', refresh);
      clearInterval(t);
    };
  }, [load]);

  const punch = useCallback(async (action, extra = {}) => {
    setChooser(null);
    setBusy(action);
    const clientPunchId = newClientPunchId();
    const at = new Date();
    try {
      const res = await api.punch({ action, client_punch_id: clientPunchId, device_id: deviceId(), ...extra });
      if (res.status === 'applied') toast.success(res.message);
      else toast.warning(res.message);
      if (res.state_after) setInfo(res.state_after);
      else load();
    } catch (e) {
      if (e.network && claims) {
        await queuePunch({ auth: 'staff', restaurantId: claims.restaurantId, who: { staffId: claims.staffId },
                           action, breakPaid: !!extra.break_paid, roleId: extra.role_id || null, clientPunchId, at });
        toast.info(`${ACTION_LABEL[action]} saved on this phone — it is sent when you are back online.`);
      } else if (e.code === 'role_required') {
        setChooser({ roles: e.detail.roles });
      } else {
        toast.error(e.message);
      }
    } finally {
      setBusy(null);
    }
  }, [api, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const clockIn = useCallback(() => {
    const roles = info?.employee?.roles || [];
    if (roles.length > 1) setChooser({ roles });
    else punch('CLOCK_IN');
  }, [info, punch]);

  useEffect(() => {
    const onAction = (e) => {
      const action = e.detail?.action;
      if (action === 'CLOCK_IN') clockIn();
      else if (action) punch(action);
    };
    window.addEventListener(MY_CLOCK_ACTION, onAction);
    return () => window.removeEventListener(MY_CLOCK_ACTION, onAction);
  }, [clockIn, punch]);

  if (!claims) return null;
  const st = info?.state || 'OFF';
  const allowed = info?.allowed_actions || (info ? [] : ['CLOCK_IN', 'BREAK_START', 'BREAK_END', 'CLOCK_OUT']);
  const tz = info?.timezone;
  const btn = 'flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white transition active:scale-[0.98] disabled:opacity-50';

  return (
    <section className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10" data-testid="my-clock">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15"><Timer className="w-5 h-5 text-emerald-300" /></span>
          <div>
            <p className="text-white font-semibold">My time clock</p>
            <p className="text-xs text-slate-400">
              {info?.entry ? `Clocked in ${fmtTime(info.entry.clock_in, tz)}${info.entry.role_name ? ` · ${info.entry.role_name}` : ''}` : 'Not on the clock'}
              {info?.on_break ? ` · ${info.on_break.is_paid ? 'paid' : 'unpaid'} break since ${fmtTime(info.on_break.start_at, tz)}` : ''}
            </p>
          </div>
        </div>
        <div className="text-right">
          <span className={`rounded-full px-2.5 py-1 text-xs ${BADGE[st]}`} data-testid="my-clock-state">{STATE_LABEL[st]}</span>
          <p className="mt-1 text-xs text-slate-400">Today <span className="text-white font-medium" data-testid="my-clock-today">{fmtDuration(info?.today?.worked_seconds)}</span></p>
        </div>
      </div>
      {error && (
        <p className="mt-3 flex items-start gap-2 text-xs text-amber-200">
          {error.startsWith('Offline') ? <CloudOff className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />} {error}
        </p>
      )}
      {pending > 0 && <p className="mt-2 text-xs text-sky-300">{pending} punch{pending === 1 ? '' : 'es'} waiting to be sent.</p>}
      {chooser?.roles ? (
        <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="my-clock-roles">
          {chooser.roles.map((r) => (
            <button key={r.id} type="button" onClick={() => punch('CLOCK_IN', { role_id: r.id })} className={`${btn} bg-emerald-600 hover:bg-emerald-500`}>
              <Briefcase className="w-4 h-4" /> {r.name}
            </button>
          ))}
        </div>
      ) : chooser === 'break' ? (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <button type="button" onClick={() => punch('BREAK_START', { break_paid: true })} className={`${btn} bg-sky-600 hover:bg-sky-500`} data-testid="my-break-paid">
            <Coffee className="w-4 h-4" /> Paid rest break
          </button>
          <button type="button" onClick={() => punch('BREAK_START', { break_paid: false })} className={`${btn} bg-amber-600 hover:bg-amber-500`} data-testid="my-break-unpaid">
            <UtensilsCrossed className="w-4 h-4" /> Unpaid meal break
          </button>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-2">
          {allowed.includes('CLOCK_IN') && (
            <button type="button" onClick={clockIn} disabled={!!busy} className={`${btn} col-span-2 bg-emerald-600 hover:bg-emerald-500`} data-testid="my-clock-in">
              {busy === 'CLOCK_IN' ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Clock in
            </button>
          )}
          {allowed.includes('BREAK_START') && (
            <button type="button" onClick={() => setChooser('break')} disabled={!!busy} className={`${btn} bg-amber-600 hover:bg-amber-500`} data-testid="my-break-start">
              <Coffee className="w-4 h-4" /> Start break
            </button>
          )}
          {allowed.includes('BREAK_END') && (
            <button type="button" onClick={() => punch('BREAK_END')} disabled={!!busy} className={`${btn} bg-emerald-600 hover:bg-emerald-500`} data-testid="my-break-end">
              {busy === 'BREAK_END' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} End break
            </button>
          )}
          {allowed.includes('CLOCK_OUT') && (
            <button type="button" onClick={() => punch('CLOCK_OUT')} disabled={!!busy} className={`${btn} bg-rose-600 hover:bg-rose-500`} data-testid="my-clock-out">
              {busy === 'CLOCK_OUT' ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />} Clock out
            </button>
          )}
        </div>
      )}
    </section>
  );
}
